import { FormDefinition } from 'app/form_config/FormController';
import { createCesiumColorForStrOrArrayColor } from 'app/shared/utilities/ims_color';
import { ImsIconUrlLookup, ImsIconUrlLookupKeyType } from '../icons/ims_icon_url_lookup';
import { AgAssetTypeConfig } from '../utilities/ag_consts';
import { toSnakeCase } from '../utilities/text_utils';
import { ImsObject } from './ims_object';

export const MOBILITY_IMMOBILE = 0;
export const MOBILITY_SEMI_MOBILE = 1;

export const enum AssetTypeMobility {
    IMMOBILE = 0,
    SEMI_MOBILE = 1,
    MOBILE = 2,
}

export const IMS_TYPE__ASSET_TYPE = 'asset_type';

/**
 * @ngdoc service
 * @name ims.shared.data.service:AssetType
 * @description
 * The ims AssetType object.
 * Object structure:
 * {id: 1,
 *  resource:{}}
 *
 * @param {ngResource} assetTypeResource An asset type ngResource object.
 */
export class AssetType extends ImsObject {
    public show: boolean;
    private _imageName: ImsIconUrlLookupKeyType;
    _cesiumColor: Cesium.Color;
    /**
     * the form definitions that was dynamically generated from the dynamic stat schemas
     */
    _dynamicallyGeneratedForms: Array<FormDefinition>;
    /**
     * This will check for a config from the model, if not present will return sensible defaults
     */
    public get ag_config(): AgAssetTypeConfig {
        if (!this.resource.ag_config) {
            return {
                category: 'object',
                hidden_in_side_drawer: true,
                hidden_in_entity_management_list: true,
                hidden_in_top_bar_list: true,
            };
        }
        return this.resource.ag_config;
    }

    public get key(): string {
        return toSnakeCase(this.name);
    }
    public get icon(): string {
        return ImsIconUrlLookup[this.image_name];
    }
    public toString(): string {
        return this.name;
    }
    public resource: {
        name: string;
        mobility: any;
        selectable: boolean;
        color: {
            cesium: string;
            name: string;
        };
        map_appearance: {
            fill_color: string;
            fill_alpha: number;
            outline_color: string;
            outline_thickness: number;
            line_thickness: number;
            line_color: string;
            z_index: number;
            overlay_image: string;
        };
        forms: Array<FormDefinition>;
        height: number;
        ag_config: AgAssetTypeConfig | null;
    };

    constructor(resource) {
        super(IMS_TYPE__ASSET_TYPE, resource);

        // Referenced to determine if assets should be shown or hidden
        this.show = true;
        this._imageName = AssetType._getNewImageName(resource);
        this._cesiumColor = createCesiumColorForStrOrArrayColor(this.cesiumColorString);
    }

    applyResourceChange(resource) {
        super.applyResourceChange(resource);
        this._imageName = AssetType._getNewImageName(resource);
    }

    static _getNewImageName(resource): ImsIconUrlLookupKeyType {
        return resource.image_name
            ? resource.image_name.replace(/^.*[\\\/]/, '').replace('.svg', '')
            : 'building'; // apparently this isn't actually used, but it still breaks stuff lol
    }

    get cesiumColorString(): string {
        return this.resource.color ? this.resource.color.cesium : 'WHITE';
    }

    get mapAppearanceFillAlpha(): number | undefined {
        return this.resource.map_appearance?.fill_alpha;
    }

    get mapAppearanceZIndex(): number | undefined {
        return this.resource.map_appearance?.z_index;
    }

    get mapAppearanceLineThickness(): number | undefined {
        return this.resource.map_appearance?.line_thickness;
    }

    get mapAppearanceOutlineThickness(): number | undefined {
        return this.resource.map_appearance?.outline_thickness;
    }

    get mapAppearanceOutlineColor(): Cesium.Color | undefined {
        if (!this.resource.map_appearance?.outline_color) {
            return undefined;
        }
        return createCesiumColorForStrOrArrayColor(this.resource.map_appearance?.outline_color);
    }

    get mapAppearanceFillColor(): Cesium.Color | undefined {
        if (!this.resource.map_appearance?.fill_color) {
            return undefined;
        }
        return createCesiumColorForStrOrArrayColor(this.resource.map_appearance?.fill_color);
    }

    get mapAppearanceLineColor(): Cesium.Color | undefined {
        if (!this.resource.map_appearance?.line_color) {
            return undefined;
        }
        return createCesiumColorForStrOrArrayColor(this.resource.map_appearance?.line_color);
    }

    get cesiumColor(): Cesium.Color {
        return this._cesiumColor;
    }

    get mapAppearanceImageOverlay(): string {
        return this.resource.map_appearance?.overlay_image;
    }

    get name() {
        return this.resource.name;
    }

    get mobility(): AssetTypeMobility {
        return this.resource.mobility;
    }

    get image_name() {
        return this._imageName;
    }

    get selectable() {
        return this.resource.selectable;
    }

    get forms(): Array<FormDefinition> {
        return this.resource.forms || [];
    }

    get dynamicallyGeneratedForms(): Array<FormDefinition> {
        // if the forms have not been generated yet -> populate them!!
        return this._dynamicallyGeneratedForms;
    }

    set dynamicallyGeneratedForms(dynamicallyGeneratedForms: Array<FormDefinition>) {
        this._dynamicallyGeneratedForms = dynamicallyGeneratedForms;
    }

    hasPopulatedDynamicallyGeneratedForms() {
        return this._dynamicallyGeneratedForms !== undefined;
    }

    get height() {
        return this.resource.height;
    }

    static toFilterable(assetType: AssetType): { name: string } {
        return {
            name: assetType.name,
        };
    }
}
